// RestStepMobile.jsx
import React, { useEffect, useMemo } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import SelectableCard from "./SelectableCard";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// spring + thresholds (tweak to taste)
const SPRING = { type: "spring", stiffness: 420, damping: 40, mass: 0.6 };
const DIST_THRESHOLD = 60;   // px
const VEL_THRESHOLD = 300;   // px/s

const RestStepMobile = ({
  items,
  currentPage,
  totalPages,
  canPrev,
  canNext,
  setCurrentPage,
  onSwipeEnd,
  onItemClick,
  canShow,
}) => {
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const x = useMotionValue(0);

  // when page changes (via arrows/programmatically) — ensure centered
  useEffect(() => {
    animate(x, 0, SPRING);
  }, [currentPage, x]);

  const handleDragEnd = (_evt, info) => {
    const { offset, velocity } = info;
    const dx = offset.x;
    const vx = velocity.x;

    let next = currentPage;

    // swipe left → next
    if ((dx < -DIST_THRESHOLD || vx < -VEL_THRESHOLD) && canNext) {
      next = Math.min(totalPages - 1, currentPage + 1);
    }
    // swipe right → prev
    if ((dx > DIST_THRESHOLD || vx > VEL_THRESHOLD) && canPrev) {
      next = Math.max(0, currentPage - 1);
    }

    if (next !== currentPage) setCurrentPage(next);

    // always snap back to center after deciding
    animate(x, 0, SPRING);

    // keep your original callback behavior
    if (onSwipeEnd) onSwipeEnd(_evt, info);
  };

  return (
    <div className="rest-matrix-mobile">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        drag="x"
        style={{
          x, // ← smooth drag + snap
          touchAction: "pan-y",
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 12,
        }}
        dragElastic={0.2}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        dir="rtl"
      >
        {safeItems.map((item) => (
          <motion.div key={item.id} variants={cardVariants}>
            <SelectableCard item={item} onClick={onItemClick} />
          </motion.div>
        ))}
      </motion.div>

      {canShow && (
        <div className="matrix-pager" style={{ marginTop: 12 }}>
          <button
            className="matrix-pager-btn"
            onClick={() =>
              canNext && setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
            }
            disabled={!canNext}
            aria-label="التالي"
          >
            <FiChevronRight size={28} />
          </button>

          <span className="matrix-pager-label">
            {totalPages} / {currentPage + 1}
          </span>

          <button
            className="matrix-pager-btn"
            onClick={() => canPrev && setCurrentPage((p) => Math.max(0, p - 1))}
            disabled={!canPrev}
            aria-label="السابق"
          >
            <FiChevronLeft size={28} />
          </button>
        </div>
      )}
    </div>
  );
};

export default RestStepMobile;
